/**
 * A todo-form picker field. Native: an input-styled button that names its value, plus the platform
 * picker — iOS toggles an inline spinner, which needs its own Done (see `showDone`), and Android
 * mounts the system dialog on demand. The form owns `open`, so only one picker is ever up. Web: the
 * browser's own input is the field (a raw <input>, utils/web-mocks/datetimepicker.web.js), always
 * mounted — it shows the value, opens the browser's picker and takes typing, so a button naming the
 * value above it would only repeat it. DateField and TimeField are its two faces.
 *
 * Renders its parts (native: field + picker group) straight into the form body, so they keep the
 * body's field rhythm exactly as when they lived inline there.
 */
import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useWebDateInputStyle } from '@/hooks/shared/useWebDateInputStyle';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';

interface PickerFieldProps {
  /**
   * The field's name: "Due date", "Time". Native speaks it with the value ("Due date: Wed, Aug
   * 12"); on web it names the input, which speaks its own value.
   */
  label: string;
  /** What the field shows. */
  shown: string;
  /** What the iOS Done closes: "due date" → "Done choosing due date". */
  pickerName: string;
  mode: 'date' | 'time';
  /** The Date the picker shows. */
  pickerValue: Date;
  /** Native: whether the picker is up. Web keeps it mounted regardless. */
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  /** A picked Date. */
  onPick: (picked: Date) => void;
}

export const PickerField: React.FC<PickerFieldProps> = ({
  label,
  shown,
  pickerName,
  mode,
  pickerValue,
  open,
  onToggle,
  onClose,
  onPick,
}) => {
  const type = useTypography();
  const space = useSpacing();
  const radius = useRadius();
  const webDateInputStyle = useWebDateInputStyle();
  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      field: {
        backgroundColor: semantic.background.input,
        borderColor: semantic.border.input,
        borderWidth: 1,
        borderRadius: radius.md,
        paddingHorizontal: space.md,
        paddingVertical: space.sm,
      },
      fieldText: {
        ...type.body,
        color: colors.text.primary,
      },
      // Tighter than the body's `lg` field rhythm so the Done affordance reads
      // as part of the picker rather than as another form field.
      pickerGroup: {
        gap: space.xs,
      },
      pickerDone: {
        alignSelf: 'flex-end',
        paddingHorizontal: space.sm,
        paddingVertical: space.xs,
      },
      pickerDoneText: {
        ...type.button,
        // `primaryHover`, not `primary`. As an INK on the sheet's bg.modal,
        // primary measures 3.99:1 in dark — under the 4.5:1 floor — while
        // primaryHover is the AA-safe blue in both modes (6.69:1 light,
        // 5.96:1 dark). The maintenance picker this flow was modeled on
        // still reaches for `primary` here and has the same gap.
        color: semantic.interactive.primaryHover,
      },
    }),
  );

  // Only the iOS wheel needs an explicit dismiss: it renders INLINE and fires
  // `onChange` on every tick, with no terminal "user picked" event. Android's
  // dialog reports one selection (closed in onChange below); on web the
  // picker is permanently mounted.
  const showDone = Platform.OS === 'ios' && open;
  const isWeb = Platform.OS === 'web';

  return (
    <>
      {!isWeb && (
        <Hoverable
          hover="field"
          style={styles.field}
          onPress={onToggle}
          accessibilityRole="button"
          accessibilityLabel={`${label}: ${shown}`}
        >
          <Text style={styles.fieldText}>{shown}</Text>
        </Hoverable>
      )}
      {(isWeb || open) && (
        <View style={styles.pickerGroup}>
          {/* Above the wheel, not below it: the wheel is ~200pt tall inside a
              body that scrolls, so a Done placed under it can open below the
              fold — while up here it's guaranteed visible the moment the tap
              that opened the picker lands. Also matches the iOS accessory-
              toolbar convention the maintenance picker follows. */}
          {showDone && (
            <Hoverable
              hover="link"
              style={styles.pickerDone}
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel={`Done choosing ${pickerName}`}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              {({ textStyle }) => <Text style={[styles.pickerDoneText, textStyle]}>Done</Text>}
            </Hoverable>
          )}
          <DateTimePicker
            value={pickerValue}
            mode={mode}
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            // Web only (undefined on native): the raw <input> the web shim
            // renders takes the browser's face and colors unless told otherwise.
            style={webDateInputStyle}
            // Web only: the input is the field there, so it carries the
            // field's name. Native names the button above instead.
            accessibilityLabel={isWeb ? label : undefined}
            onChange={(_event: unknown, picked?: Date) => {
              if (Platform.OS === 'android') onClose();
              if (picked) onPick(picked);
            }}
          />
        </View>
      )}
    </>
  );
};

PickerField.displayName = 'PickerField';
